import { FC } from 'react';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  MonitorUp,
  Volume2,
  VolumeX,
  Camera,
} from 'lucide-react';
import { cn } from '@new/lib';

interface MediaState {
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  isSpeakerEnabled: boolean;
  isScreenSharing: boolean;
}

interface MediaControlsProps {
  mediaState: MediaState;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
  onToggleSpeaker: () => void;
  onToggleScreenShare: () => void;
  onHangUp: () => void;
  onTakePhoto?: () => void;
  isInstructor?: boolean;
  microphones?: MediaDeviceInfo[];
  activeMicId?: string;
  onSwitchMicrophone?: (deviceId: string) => void;
  speakers?: MediaDeviceInfo[];
  activeSpeakerId?: string;
  onSwitchSpeaker?: (deviceId: string) => void;
  cameras?: MediaDeviceInfo[];
  activeCameraId?: string;
  onSwitchCamera?: (deviceId: string) => void;
}

interface ControlButtonProps {
  onClick: () => void;
  isActive?: boolean;
  variant?: 'default' | 'danger';
  'aria-label': string;
  children: React.ReactNode;
}

const ControlButton: FC<ControlButtonProps> = ({
  onClick,
  isActive = true,
  variant = 'default',
  'aria-label': ariaLabel,
  children,
}) => (
  <button
    onClick={onClick}
    aria-label={ariaLabel}
    className={cn(
      'flex items-center justify-center w-12 h-12 rounded-full! transition-colors cursor-pointer',
      variant === 'danger'
        ? 'bg-(--danger-D700)! hover:bg-(--danger-D600)! text-(--neutral-white-NW1000)'
        : isActive
          ? 'bg-(--neutral-white-NW1000) hover:bg-(--neutral-white-NW800) text-(--neutral-white-NW600)'
          : 'bg-(--neutral-white-NW800) text-(--neutral-white-NW600)'
    )}
  >
    {children}
  </button>
);

export const MediaControls: FC<MediaControlsProps> = ({
  mediaState,
  onToggleVideo,
  onToggleAudio,
  onToggleSpeaker,
  onToggleScreenShare,
  onHangUp,
  onTakePhoto,
  isInstructor = false,
}) => {
  return (
    <div className="flex items-center justify-center gap-4 py-4">
      <ControlButton
        onClick={onToggleVideo}
        isActive={mediaState.isVideoEnabled}
        aria-label={mediaState.isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
      >
        {mediaState.isVideoEnabled ? <Video size={20} /> : <VideoOff size={20} />}
      </ControlButton>

      <ControlButton
        onClick={onToggleAudio}
        isActive={mediaState.isAudioEnabled}
        aria-label={mediaState.isAudioEnabled ? 'Mute microphone' : 'Unmute microphone'}
      >
        {mediaState.isAudioEnabled ? <Mic size={20} /> : <MicOff size={20} />}
      </ControlButton>

      <ControlButton onClick={onHangUp} variant="danger" aria-label="Hang up">
        <Phone size={20} color="white" fill="white" />
      </ControlButton>

      {isInstructor && onTakePhoto && (
        <ControlButton onClick={onTakePhoto} aria-label="Take screenshot">
          <Camera size={20} />
        </ControlButton>
      )}

      <ControlButton
        onClick={onToggleScreenShare}
        isActive={mediaState.isScreenSharing}
        aria-label={mediaState.isScreenSharing ? 'Stop screen share' : 'Share screen'}
      >
        <MonitorUp size={20} />
      </ControlButton>

      <ControlButton
        onClick={onToggleSpeaker}
        isActive={mediaState.isSpeakerEnabled}
        aria-label={mediaState.isSpeakerEnabled ? 'Mute speaker' : 'Unmute speaker'}
      >
        {mediaState.isSpeakerEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
      </ControlButton>
    </div>
  );
};
